'use client';

import Head from 'next/head';

/**
 * SEO Head Component - Atom Design Pattern
 *
 * A component that handles dynamic SEO meta tags from Strapi
 * Includes title, description, keywords, and Open Graph tags
 */

export interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  siteName?: string;
  url?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  image,
  url,
  siteName,
}: SEOHeadProps) {
  // Use the provided title directly if it already includes the site name
  // Otherwise, construct it with the site name
  const fullTitle = title
    ? title.includes(siteName || '')
      ? title
      : `${title} | ${siteName}`
    : siteName;
  const fullUrl = url;

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      {description && <meta name='description' content={description} />}
      {keywords && <meta name='keywords' content={keywords} />}

      {/* Open Graph Meta Tags */}
      <meta property='og:title' content={fullTitle} />
      {description && <meta property='og:description' content={description} />}
      {image && <meta property='og:image' content={image} />}
      <meta property='og:type' content='website' />
      <meta property='og:url' content={fullUrl} />
      <meta property='og:site_name' content={siteName} />

      {/* Twitter Card Meta Tags */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      {description && <meta name='twitter:description' content={description} />}
      {image && <meta name='twitter:image' content={image} />}

      {/* Additional Meta Tags */}
      <meta name='viewport' content='width=device-width, initial-scale=1' />
      <meta charSet='utf-8' />
      <meta name='robots' content='index, follow' />
    </Head>
  );
}
